import React, { useEffect, useState } from 'react';
import {
  GeocodeBackfillResult,
  GeocodeProgress,
  runGeocodeBackfill,
} from '../../services/geocodeBackfill';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import { X, Loader2, MapPin, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * Einstieg in den Verortungs-Lauf (ERP/CRM-Plan Phase 0).
 *
 * Duenn gehalten: die gesamte Fachlogik steckt in
 * services/geocodeBackfill.ts. Hier steht nur der Ablauf
 * Vorschau (dry-run) -> Start -> Fortschritt -> Fehlerliste.
 *
 * Warum ueberhaupt eine Vorschau: der Lauf ist mit ~1 Sekunde je unbekannter
 * Adresse langsam (Nominatim-Limit). Wer vorher sieht, dass 400 Adressen offen
 * sind, weiss, dass er sieben Minuten einplanen muss.
 */

interface Props {
  onClose: () => void;
  /** Nach dem Lauf die Kundenliste neu laden — die Koordinaten sind neu. */
  onFinished: () => void;
}

const zeitschaetzung = (offen: number): string => {
  if (offen <= 0) return '';
  const sekunden = Math.ceil(offen * 1.1);
  if (sekunden < 90) return `ca. ${sekunden} Sekunden`;
  return `ca. ${Math.ceil(sekunden / 60)} Minuten`;
};

export const GeocodeBackfillModal: React.FC<Props> = ({ onClose, onFinished }) => {
  const [vorschau, setVorschau] = useState<GeocodeBackfillResult | null>(null);
  const [ergebnis, setErgebnis] = useState<GeocodeBackfillResult | null>(null);
  const [fortschritt, setFortschritt] = useState<GeocodeProgress | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Vorschau beim Oeffnen: zaehlt nur, fragt nichts ab, schreibt nichts.
  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const res = await runGeocodeBackfill({ dryRun: true });
        if (!abgebrochen) setVorschau(res);
      } catch (e) {
        if (!abgebrochen) setFehler(`Die Kundenliste konnte nicht geprüft werden: ${rechtFehlerOder(e)}`);
      }
    })();
    return () => { abgebrochen = true; };
  }, []);

  const starten = async () => {
    setLaeuft(true);
    setFehler(null);
    setFortschritt(null);
    try {
      const res = await runGeocodeBackfill({ onProgress: setFortschritt });
      setErgebnis(res);
      onFinished();
    } catch (e) {
      setFehler(`Die Verortung wurde abgebrochen: ${rechtFehlerOder(e)}`);
    } finally {
      setLaeuft(false);
    }
  };

  const offen = vorschau?.candidates.length ?? 0;
  const anzeige = ergebnis ?? vorschau;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
        <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><MapPin className="w-6 h-6 text-blue-400" /></div>
            <div>
              <h3 className="text-lg font-bold">Adressen verorten</h3>
              <p className="text-xs text-slate-400">Koordinaten für die Kundenkarte — einmalig je Adresse</p>
            </div>
          </div>
          <button onClick={onClose} disabled={laeuft} className="p-2 rounded-xl hover:bg-white/10 disabled:opacity-40" title={laeuft ? 'Läuft noch…' : 'Schließen'}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {fehler && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{fehler}</span>
            </div>
          )}

          {!anzeige && !fehler && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Kundenliste wird geprüft…
            </div>
          )}

          {anzeige && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {([
                ['Offen', ergebnis ? ergebnis.candidates.length : offen],
                ['Bereits verortet', anzeige.skippedGeocoded],
                ['Ohne Adresse', anzeige.skippedWithoutAddress],
                ['Manuell gesetzt', anzeige.skippedManual],
              ] as const).map(([label, wert]) => (
                <div key={label} className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-xl font-bold text-slate-800">{wert}</div>
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
                </div>
              ))}
            </div>
          )}

          {!ergebnis && offen > 0 && !laeuft && (
            <p className="text-xs text-slate-500">
              {offen} Adresse{offen === 1 ? '' : 'n'} werden bei OpenStreetMap abgefragt — höchstens eine pro Sekunde
              (Nutzungsbedingungen). Dauer: {zeitschaetzung(offen)}. Bereits bekannte Adressen kommen aus dem Zwischenspeicher.
              Von Hand gesetzte Koordinaten bleiben unangetastet.
            </p>
          )}

          {laeuft && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                {fortschritt ? `${fortschritt.done} von ${fortschritt.total}` : 'Start…'}
                {fortschritt?.current ? ` — ${fortschritt.current}` : ''}
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${fortschritt && fortschritt.total ? Math.round((fortschritt.done / fortschritt.total) * 100) : 0}%` }}
                />
              </div>
            </div>
          )}

          {ergebnis && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                {ergebnis.updated} Kunde{ergebnis.updated === 1 ? '' : 'n'} verortet
                {ergebnis.fromCache > 0 ? ` (davon ${ergebnis.fromCache} aus dem Zwischenspeicher)` : ''}.
              </span>
            </div>
          )}

          {ergebnis && ergebnis.failures.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-700">
                <AlertTriangle className="w-4 h-4" />
                {ergebnis.failures.length} Adresse{ergebnis.failures.length === 1 ? '' : 'n'} zur Nacharbeit
              </div>
              <p className="text-xs text-slate-500">
                Diese Adressen sind bei OpenStreetMap nicht auffindbar. Im Kundendetail korrigieren und den Lauf erneut starten.
              </p>
              <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-200 divide-y divide-slate-100">
                {ergebnis.failures.map((f) => (
                  <div key={f.customerId} className="p-3 text-xs">
                    <div className="font-bold text-slate-700">{f.name}</div>
                    <div className="text-slate-500">{f.address || 'ohne Adresse'}</div>
                    <div className="text-amber-600">{f.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end gap-3 shrink-0">
          <Button variant="secondary" onClick={onClose} disabled={laeuft}>
            {ergebnis ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!ergebnis && (
            <Button
              variant="primary"
              onClick={starten}
              disabled={laeuft || offen === 0}
              icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            >
              {offen === 0 ? 'Nichts zu tun' : `${offen} verorten`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
